import { IconTrash } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import { useContextMenu } from 'mantine-contextmenu';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { SerializableRenderTreeNode } from '../../../../../react-commons/components/data-display/tree/RenderTreeNode';
import { Tree } from '../../../../../react-commons/components/data-display/tree/Tree';
import { TreeHoverButton } from '../../../../../react-commons/components/inputs/buttons/TreeHoverButton';
import { useNestedDialogFunction } from '../../../../../react-commons/dialog-provider/UseNestedDialogFunction';
import { SplitPane } from '../../../../../react-commons/views/split-pane/SplitPane';
import { ResourceType } from '../../../resource/enumerations/ResourceType';
import {
    type ProjectUserGroupResource,
    useProjectUserGroupActions,
} from '../../../resource/resources/persistables/user/projectUserGroupResource';
import { useProjectUserGroupRoleAssignmentActions } from '../../../resource/resources/persistables/user/projectUserGroupRoleAssignmentResource';
import { CreateProjectUserGroupDialog } from '../../page-components/project-user-management/project-user-group/CreateProjectUserGroupDialog';
import { ProjectUserGroupView } from '../../page-components/project-user-management/project-user-group/ProjectUserGroupView';
import { useDeleteResourceDialogFunction } from '../../reusable-components/dialogs/UseDeleteResourceDialogFunction';
import { NothingSelectedComponent } from '../../reusable-components/NothingSelectedComponent';
import { checkUnsavedData } from '../../util/checkUnsavedData';
import { defaultLoadingNode } from '../../util/parseTreeFunctions';
import { selectIsDirty } from '../../util/useSelectorFunctions';
import { selectProjectUserGroup } from './ProjectUserManagementActions';

type ProjectUserGroupsPageTabTreeNode = SerializableRenderTreeNode<ProjectUserGroupResource>;

const parseProjectUserGroups = (groupOutlines: QueryObserverResult<Array<ProjectUserGroupResource> | null>) => {
    return (
        groupOutlines.data
            ?.toSorted((a, b) => a.name.localeCompare(b.name))
            .map((group) => ({
                key: group.id,
                label: group.name,
                data: group,
            })) ?? [defaultLoadingNode]
    );
};

export const ProjectUserGroupsPageTab = ({ projectId }: { projectId: string }) => {
    const [selectedNode, setSelectedNode] = React.useState<ProjectUserGroupsPageTabTreeNode | null>(null);
    const [nodes, setNodes] = React.useState(new Array<ProjectUserGroupsPageTabTreeNode>());

    const dispatch = useDispatch();
    const isDirty = useSelector(selectIsDirty);

    const nestedDialog = useNestedDialogFunction();
    const { showContextMenu } = useContextMenu();
    const deleteResourceDialogFunction = useDeleteResourceDialogFunction();

    const projectUserGroupRoleAssignmentActions = useProjectUserGroupRoleAssignmentActions();
    const projectUserGroupActions = useProjectUserGroupActions();

    const roleAssignments = projectUserGroupRoleAssignmentActions.useGetAllResources(projectId);
    const deleteProjectUserGroup = projectUserGroupActions.useDeleteResource();
    const projectUserGroupOutlines = projectUserGroupActions.useGetAllResources(projectId);

    React.useEffect(() => {
        const newNodes = parseProjectUserGroups(projectUserGroupOutlines);
        setNodes(newNodes);

        if (projectUserGroupOutlines.data && !selectedNode && newNodes.length) {
            setSelectedNode(newNodes[0]);
        }
    }, [projectUserGroupOutlines.data]);

    const openAddDialog = () => {
        nestedDialog.showNestedDialog(
            <CreateProjectUserGroupDialog onClose={nestedDialog.closeNestedDialog} parentId={projectId} />
        );
    };

    const deleteGroup = (id: string, name: string) => {
        deleteResourceDialogFunction.deleteResource(
            id,
            name,
            ResourceType.PROJECT_USER_GROUP,
            deleteProjectUserGroup,
            () => {
                dispatch(selectProjectUserGroup('', ''));
                setSelectedNode(null);
            }
        );
    };

    const getMenuItems = (node: ProjectUserGroupsPageTabTreeNode) => {
        return [
            {
                icon: <IconTrash />,
                key: 'delete',
                title: 'Delete Project User Group',
                onClick: () => deleteGroup(node.key, node.label),
            },
        ];
    };

    return (
        <div className={'main-window-content'} style={{ height: 'calc(100% - 110px)', position: 'absolute' }}>
            <SplitPane secondaryInitialSize={70}>
                <div>
                    <Tree
                        nodes={nodes}
                        filtered
                        selectedNodeKey={selectedNode?.key}
                        onNodeSelected={(node) => {
                            checkUnsavedData(isDirty, () => {
                                setSelectedNode(node);
                                dispatch(selectProjectUserGroup(node.key, node.label));
                            });
                        }}
                        onContextMenu={(selection) => showContextMenu(getMenuItems(selection.node))(selection.event)}
                        onAddClick={openAddDialog}
                        getElements={(node) => ({
                            hoverNode: (
                                <TreeHoverButton
                                    Icon={IconTrash}
                                    onClick={() => deleteGroup(node.data.id, node.data.name)}
                                />
                            ),
                        })}
                    />
                </div>
                <div
                    style={{
                        display: 'inline-block',
                        verticalAlign: 'top',
                        overflowY: 'auto',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {selectedNode ?
                        <ProjectUserGroupView
                            id={selectedNode.key}
                            projectUserGroupRoleAssignments={roleAssignments.data ?? []}
                            parentId={projectId}
                        />
                    :   <NothingSelectedComponent />}
                </div>
            </SplitPane>
        </div>
    );
};
